import {
  GraphDocSchema, StudioDesktopSessionSchema, StudioFrameInputSchema, StudioFrameOutputSchema,
  StudioParametersSchema, StudioProjectSchema, StudioToolRequestSchema, StudioToolResultSchema, StudioWebPageSchema,
  type StudioAsset, type StudioDesktopSession, type StudioFrameInput, type StudioFrameOutput,
  type StudioGraph, type StudioProject, type StudioWebPage,
} from '@genieartinc/schemas/graph';
import type { StudioAssetReader, StudioProjectStore, StudioToolExecutor } from '@genieartinc/core-ports';

function canonicalJson(value: unknown): string {
  const ordered = (item: unknown): unknown => Array.isArray(item) ? item.map(ordered)
    : item && typeof item === 'object' ? Object.fromEntries(Object.entries(item).sort(([a], [b]) => a.localeCompare(b)).map(([key, child]) => [key, ordered(child)])) : item;
  return JSON.stringify(ordered(value));
}

/** Validate before cloning: JSON.stringify alone silently drops unsupported values. */
export function readStudioProject(value: unknown): StudioProject {
  StudioParametersSchema.parse(value);
  return StudioProjectSchema.parse(value);
}

/** Keep renderer-specific layout, handles, groups and data separate from execution. */
export function webPageToStudioGraph(id: string, name: string, value: unknown): StudioGraph {
  StudioParametersSchema.parse(value);
  const { nodes, edges, ...page } = StudioWebPageSchema.parse(value);
  const doc = GraphDocSchema.parse({
    webPage: page,
    nodes: nodes.map(({ id: nodeId, type, position, data, ...renderer }) => ({
      id: nodeId, type: `web:${type}`, x: position.x, y: position.y, params: data,
      web: { ...renderer, position },
    })),
    edges: edges.map(({ id: edgeId, source, target, sourceHandle, targetHandle, ...renderer }) => ({
      id: edgeId, source, target,
      ...(typeof sourceHandle === 'string' ? { sourceHandle } : {}),
      ...(typeof targetHandle === 'string' ? { targetHandle } : {}),
      web: { ...renderer, ...(sourceHandle === null ? { sourceHandle: null } : {}), ...(targetHandle === null ? { targetHandle: null } : {}) },
    })),
  });
  return { id, name, source: 'web', doc };
}

export function studioGraphToWebPage(graph: StudioGraph): StudioWebPage {
  if (graph.source !== 'web') throw new Error('This graph requires its desktop editor; it cannot be converted into web nodes.');
  GraphDocSchema.parse(graph.doc);
  const page = StudioParametersSchema.parse(graph.doc.webPage ?? {});
  return StudioWebPageSchema.parse({
    ...page,
    nodes: graph.doc.nodes.map(({ id, type, x, y, params, web }) => {
      if (!type.startsWith('web:')) throw new Error(`Node ${id} has no web renderer mapping.`);
      const renderer = StudioParametersSchema.parse(web ?? {});
      return { ...renderer, id, type: type.slice(4), position: { ...StudioParametersSchema.parse(renderer.position ?? {}), x, y }, data: params };
    }),
    edges: graph.doc.edges.map(({ web, ...edge }) => ({ ...StudioParametersSchema.parse(web ?? {}), ...edge })),
  });
}

/** Asset collection is explicit: every referenced version must have verified bytes. */
export function desktopSessionToStudioProject(value: unknown, assetsBySource: ReadonlyMap<string, StudioAsset>): StudioProject {
  StudioParametersSchema.parse(value);
  const { artboards, graphs, ...session } = StudioDesktopSessionSchema.parse(value);
  const assets = new Map<string, StudioAsset>();
  const frames = artboards.map((frame) => ({
    ...frame,
    versions: frame.versions.map(({ imagePath, imageUrl, ...version }) => {
      const source = imagePath || imageUrl;
      const asset = source ? assetsBySource.get(source) : undefined;
      if (source && !asset) throw new Error(`Missing asset for frame ${frame.id}, version ${version.id}: ${source}`);
      if (asset) {
        const previous = assets.get(asset.id);
        if (previous && canonicalJson(previous) !== canonicalJson(asset)) throw new Error(`Conflicting asset identity: ${asset.id}`);
        assets.set(asset.id, asset);
      }
      return {
        ...version,
        ...(asset ? { assetId: asset.id } : {}),
        desktopMedia: { ...(imagePath !== undefined ? { imagePath } : {}), ...(imageUrl !== undefined ? { imageUrl } : {}) },
      };
    }),
  }));
  return readStudioProject({
    schemaVersion: 1, id: session.id, title: session.title, desktopSession: session,
    artboards: frames, graphs: graphs.map((graph) => ({ ...graph, source: 'desktop' })), assets: [...assets.values()],
    ...(graphs[0] ? { primaryGraphId: graphs[0].id } : {}),
  });
}

/** Rehydration is separate from storage: the caller must create a fresh session. */
export function studioProjectToDesktopSession(value: unknown, sourcesByAsset?: ReadonlyMap<string, string>): StudioDesktopSession {
  const project = readStudioProject(value);
  if (project.graphs.some((graph) => graph.source === 'web')) throw new Error('Web graphs must stay in the web editor until desktop supports them. The project is unchanged.');
  const session = StudioParametersSchema.parse(project.desktopSession ?? {});
  return StudioDesktopSessionSchema.parse({
    ...session, id: project.id, title: project.title,
    artboards: project.artboards.map((frame) => ({
      ...frame,
      versions: frame.versions.map(({ assetId, desktopMedia, ...version }) => {
        const media = StudioParametersSchema.parse(desktopMedia ?? {});
        if (assetId && sourcesByAsset) {
          const source = sourcesByAsset.get(assetId);
          if (!source) throw new Error(`Missing transferred asset: ${assetId}`);
          // The local engine expects output filenames. The renderer receives URLs from the engine.
          return { ...version, imagePath: source };
        }
        if (assetId && !Object.keys(media).length) throw new Error(`Asset ${assetId} needs a host location before it can open in desktop.`);
        return { ...version, ...media };
      }),
    })),
    graphs: project.graphs.map(({ source: _source, ...graph }) => graph),
  });
}

export function resolveStudioFrameInput(value: unknown, input: StudioFrameInput): { asset: StudioAsset; version: StudioProject['artboards'][number]['versions'][number] } {
  const project = readStudioProject(value);
  const reference = StudioFrameInputSchema.parse(input);
  const frame = project.artboards.find((candidate) => candidate.id === reference.frameId);
  const version = frame?.versions.find((candidate) => candidate.id === reference.versionId);
  if (!version) throw new Error('The selected frame version is missing. Choose an existing version.');
  const asset = project.assets.find((candidate) => candidate.id === version.assetId);
  if (!asset) throw new Error('The selected frame version has no asset.');
  return { asset, version };
}

/** Immutable, idempotent output application with an explicit stale-target check. */
export function appendStudioFrameVersion(value: unknown, output: StudioFrameOutput): StudioProject {
  const project = readStudioProject(value);
  const request = StudioFrameOutputSchema.parse(output);
  const frame = project.artboards.find((candidate) => candidate.id === request.frameId);
  if (!frame) throw new Error('The output frame no longer exists.');
  const existing = frame.versions.find((version) => version.id === request.version.id);
  if (existing) {
    if (canonicalJson(existing) === canonicalJson(request.version)) return project;
    throw new Error('A different result already uses this version ID.');
  }
  if ((frame.versions[frame.activeVersion]?.id ?? null) !== request.expectedVersionId) {
    throw new Error('The frame changed while this node ran. Select the target version again before applying the result.');
  }
  return readStudioProject({
    ...project,
    artboards: project.artboards.map((candidate) => candidate.id !== frame.id ? candidate : {
      ...candidate, versions: [...candidate.versions, request.version], activeVersion: candidate.versions.length,
    }),
  });
}

export async function verifyStudioAsset(asset: StudioAsset, bytes: Uint8Array): Promise<void> {
  if (bytes.byteLength !== asset.size) throw new Error(`Asset size does not match: ${asset.id}`);
  const digest = await crypto.subtle.digest('SHA-256', Uint8Array.from(bytes));
  const hash = [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, '0')).join('');
  if (hash !== asset.sha256) throw new Error(`Asset checksum does not match: ${asset.id}`);
}

/** Destination storage becomes visible only after every asset has been verified. */
export async function transferStudioProject(value: unknown, source: StudioAssetReader, destination: StudioProjectStore): Promise<string> {
  const project = readStudioProject(value);
  const transaction = await destination.beginImport();
  try {
    for (const asset of project.assets) {
      const bytes = await source.read(asset);
      await verifyStudioAsset(asset, bytes);
      await transaction.write(asset, bytes);
    }
    return await transaction.commit(project);
  } catch (error) {
    try { await transaction.rollback(); }
    catch (rollbackError) { throw new AggregateError([error, rollbackError], 'Import failed and staging cleanup also failed. Inspect the destination before retrying.'); }
    throw error;
  }
}

/** Capability checks happen before submitting work; no location or provider fallback. */
export function createStudioToolExecutor(adapter: StudioToolExecutor): StudioToolExecutor {
  return {
    location: adapter.location,
    available: (toolId) => adapter.available(toolId),
    async execute(value) {
      const request = StudioToolRequestSchema.parse(value);
      if (request.location !== adapter.location) throw new Error('The selected execution location is not connected.');
      if (!await adapter.available(request.toolId)) throw new Error(`Tool unavailable on ${adapter.location}: ${request.toolId}`);
      return StudioToolResultSchema.parse(await adapter.execute(request));
    },
  };
}

export { workflowImages, workflowInputs, webPagePlan, webPageRunIssues, type StudioWebNodePlan } from './workflow';
